import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Student, MagnifyingGlass } from '@phosphor-icons/react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const METHODS = [
  { v: 'cash', de: 'Bar', en: 'Cash' },
  { v: 'card', de: 'Karte', en: 'Card' },
  { v: 'twint', de: 'TWINT', en: 'TWINT' },
  { v: 'invoice', de: 'Rechnung', en: 'Invoice' },
];

// Kasse: pick a junior with an open Abo, book the payment, tick "Abo bezahlt".
export const JuniorAboPayButton = ({ onPaid }) => {
  const { API, language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);
  const [open, setOpen] = useState(false);
  const [juniors, setJuniors] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel(null); setQ(''); setAmount('');
    API.get('/academy/juniors').then(({ data }) => setJuniors(data.filter(j => j.is_active !== false && j.membership_type && !j.membership_paid))).catch(() => setJuniors([]));
  }, [open, API]);

  const pick = async (j) => {
    setSel(j);
    try {
      const { data } = await API.get(`/academy/juniors/${j.id}/abo-price`);
      setAmount(data.price != null ? String(data.price) : ''); setLabel(data.label);
    } catch { setAmount(''); setLabel(''); }
  };

  const pay = async (method) => {
    const a = parseFloat(amount);
    if (!a || a <= 0) { toast.error(L('Enter an amount', 'Bitte Betrag eingeben')); return; }
    setBusy(true);
    try {
      const { data } = await API.post(`/academy/juniors/${sel.id}/pay`, { amount: a, method });
      toast.success(data.membership_paid ? L(`${sel.name}: Abo paid`, `${sel.name}: Abo bezahlt`) : L('Invoice recorded', 'Rechnung erfasst'));
      setOpen(false); onPaid?.();
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setBusy(false); }
  };

  const list = juniors.filter(j => `${j.name} ${j.parent_name || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 font-semibold rounded-lg" data-testid="kasse-junior-abo-btn">
        <Student size={16} className="mr-2" /> {L('Junior Abo', 'Junior-Abo')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="kasse-junior-abo-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Pay Junior Abo', 'Junior-Abo bezahlen')}</DialogTitle>
            <DialogDescription>{L('Only juniors with an open Abo are listed.', 'Es werden nur Junioren mit offenem Abo angezeigt.')}</DialogDescription>
          </DialogHeader>
          {!sel ? (
            <div className="space-y-3">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-3 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder={L('Search junior or parent', 'Junior oder Eltern suchen')} data-testid="kasse-junior-search" />
              </div>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {list.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">{L('No open Abos', 'Keine offenen Abos')}</p>}
                {list.map(j => (
                  <button key={j.id} type="button" onClick={() => pick(j)} data-testid={`kasse-junior-${j.id}`}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors">
                    <div className="font-semibold text-slate-800 text-sm">{j.name}</div>
                    <div className="text-xs text-slate-500">{j.parent_name || ''}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="font-bold text-slate-900">{sel.name}</div>
                <div className="text-xs text-slate-500">{label}</div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-500">{L('Amount (CHF)', 'Betrag (CHF)')}</label>
                <Input type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="kasse-junior-amount"
                  placeholder={L('Price on request — enter amount', 'Preis auf Anfrage — Betrag eingeben')} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map(m => (
                  <Button key={m.v} onClick={() => pay(m.v)} disabled={busy} data-testid={`kasse-junior-pay-${m.v}`}
                    className={m.v === 'invoice' ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50' : 'bg-emerald-500 text-white hover:bg-emerald-600 font-bold'}>
                    {L(m.en, m.de)}
                  </Button>
                ))}
              </div>
              <Button variant="ghost" onClick={() => setSel(null)} className="w-full text-slate-500">{L('Back', 'Zurück')}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
